import { Routes, Route, useParams, useLocation, useNavigate, NavigateFunction } from 'react-router';
import CheatSheetPage from './pages/CheatSheetPage';
import LoginPage from './pages/LoginPage';
import OnboardingPage from './pages/Onboarding/OnboardingPage';
import RegisterPage from './pages/RegisterPage';
import WelcomePage from './pages/WelcomePage';
import UI from './ui';
import HomePage from './pages/MainApp/HomePage';
import PostPage from './pages/MainApp/PostPage';
import ExplorePage from './pages/MainApp/ExplorePage';
import CreatePostPage from './pages/MainApp/CreatePostPage';
import { ResponsiveAppBarProps } from './ui/ResponsiveAppBar';
import PersonalProfilePage from './pages/MainApp/PersonalProfilePage';
import ProfilePage from './pages/MainApp/ProfilePage';
import { useEffect } from 'react';

// params example: www.zylo.com/posts/:id
// query example: www.zylo.com/explore?search=value
type renderProps = (params: Record<string, string>, query: string | undefined, navigate: NavigateFunction) => React.ReactNode;
type route = {
  path: string
  render: renderProps
  hideAppBar?: boolean
  header?: renderProps
  title?: string
};

const routes: route[] = [
  {
    path: '/',
    render: () => <WelcomePage />,
    hideAppBar: true,
  },
  {
    path: '/login',
    render: () => <LoginPage />,
    hideAppBar: true,
    title: 'Log In',
  },
  {
    path: '/register',
    render: () => <RegisterPage />,
    hideAppBar: true,
    title: 'Register',
  },
  {
    path: '/onboarding',
    render: () => <OnboardingPage />,
    hideAppBar: true,
    title: 'Onboarding',
  },
  {
    path: '/home',
    render: () => <HomePage />,
    title: 'Home',
  },
  {
    path: '/posts/:id',
    render: ({ id }) => <PostPage id={parseInt(id, 10)} />,
    title: 'Post',
  },
  {
    path: '/explore',
    render: (_, query) => <ExplorePage search={query || ''} />,
    header: (_, query, navigate) => {
      return (
        <UI.Textfield
          value={query || ''}
          onChange={v => navigate('/explore?' + encodeURIComponent(v), { replace: true })}
          startIcon="search"
          placeHolder="Search for something"
        />
      );
    },
    title: 'Explore',
  },
  {
    path: '/new-post',
    render: () => <CreatePostPage />,
    header: (_, __, navigate) => {
      return (
        <UI.HorizontalLayout align="center">
          <UI.IconButton name="arrow_back_ios" onClick={() => navigate('/home')} />
          <UI.Text size="title">NEW POST</UI.Text>
        </UI.HorizontalLayout>
      );
    },
    title: 'New Post',
  },
  {
    path: '/personal-profile',
    render: () => <PersonalProfilePage />,
    title: 'My Profile',
  },
  {
    path: '/profile/:id',
    render: ({ id }) => <ProfilePage userId={id} />,
    title: 'Profile',
  },
  {
    path: '/cheat-sheet',
    render: () => <CheatSheetPage />,
    title: 'Cheat Sheet',
  },
];

const removeUndefinedValues: (obj: Record<string, string | undefined>) => Record<string, string> = (obj) => {
  return Object.entries(obj).reduce((result, [key, value]) => {
    if (value !== undefined) {
      result[key] = value;
    }
    return result;
  }, {} as Record<string, string>);
};

const appBarIcons: ResponsiveAppBarProps['items'] = [
  { icon: 'home', label: 'Home', path: '/home' },
  { icon: 'search', label: 'Explore', path: '/explore' },
  { icon: 'add', label: 'Add', path: '/new-post' },
  { icon: 'person', label: 'Profile', path: '/personal-profile' },
];

const AppPage: React.FC<{ hideAppBar: boolean, render: renderProps, header?: renderProps, title?: string }> = ({ hideAppBar, render, header, title }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const search = decodeURIComponent(location.search).replace(/^\?/, ''); // remove '?' if it's at the start of the string
  const params = removeUndefinedValues(useParams());
  const content = render(params, search, navigate);
  const headerContent = header ? header(params, search, navigate) : undefined;
  useEffect(() => {
    document.title = title ? `Zylo - ${title}` : 'Zylo';
  }, [title]);
  return (
    <UI.ResponsiveAppbar items={appBarIcons} showAppBar={!hideAppBar} header={headerContent}>
      <UI.Page>
        {/* <UI.Animate> */}
        {content}
        {/* </UI.Animate> */}
      </UI.Page>
    </UI.ResponsiveAppbar>
  );
};

export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {routes.map(({ path, render, hideAppBar, header, title }, i) => {
        return (
          <Route key={i} path={path} element={<AppPage hideAppBar={!!hideAppBar} render={render} header={header} title={title} />} />
        );
      })}
    </Routes>
  );
};
